import axios, { rawAxios } from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import { attachNextPricingBoundary } from '@/lib/services/pricingBoundary'
import type {
  CreatedProduct,
  CreateProductRequest,
  Product,
  ProductDetail,
  ProductShow,
  ProductDraftSearchTagRecommendation,
  ProductIdInput,
  ProductIndexFilters,
  ProductIndexResponse,
  ProductIndexResult,
  ProductResult,
  ProductSearchTagDraft,
  ProductSearchTagGroup,
  ProductSearchTagRecommendationResult,
  ProductShowFilters,
  UpdateProductRequest,
} from './products.types'

/** Every product request is scoped to the organization. */
export interface ProductOrganizationScope {
  organizationId: number | string
}

/** Calls that act on one product; their Laravel routes require its id. */
export interface ProductScope extends ProductOrganizationScope {
  productId: ProductIdInput
}

/** Query key entries; the scoped key builders always set the organization as a number. */
interface ProductKeyEntries {
  organizationId: number
}

export interface ProductIndexQueryKeyEntries extends ProductKeyEntries {
  filters?: ProductIndexFilters | undefined
}

export interface ProductInfiniteQueryKeyEntries extends ProductIndexQueryKeyEntries {
  pageSize?: number | undefined
}

/** `productQueryKeys.show` always sets the id the request URL names. */
export interface ProductShowQueryKeyEntries extends ProductKeyEntries {
  productId: ProductIdInput
  filters?: ProductShowFilters | undefined
}

export type ProductSearchTagsQueryKeyEntries = ProductKeyEntries

/** Query functions read the key, and the list cancels superseded requests. */
interface ProductQueryContext<TKey> {
  queryKey: [TKey]
  signal?: AbortSignal
}

const NEXT_PRICING_BOUNDARY_HEADER = 'x-next-price-adjustment-change-at'

export async function getProducts({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: ProductQueryContext<ProductIndexQueryKeyEntries>): Promise<ProductIndexResult> {
  const response = await rawAxios.get<ProductIndexResponse>(
    '/products',
    buildAdminRequestConfig({
      organizationId,
      params: { ...filters },
      ...(signal ? { signal } : {}),
    })
  )
  const nextPriceAdjustmentChangeAt: unknown = response.headers[NEXT_PRICING_BOUNDARY_HEADER]

  return Array.isArray(response.data)
    ? attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt, { shape: 'array' })
    : attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt)
}

/** Loads one page; the list sends `page`, so the API answers with a page and its meta. */
export async function getInfiniteProducts({
  pageParam = 1,
  queryKey: [{ organizationId, filters = {}, pageSize = 20 }],
  signal,
}: ProductQueryContext<ProductInfiniteQueryKeyEntries> & { pageParam?: number }) {
  return getProducts({
    queryKey: [
      {
        organizationId,
        filters: {
          ...filters,
          page: pageParam,
          page_size: pageSize,
        },
      },
    ],
    signal,
  })
}

export async function getProduct({
  queryKey: [{ organizationId, productId, filters = {} }],
}: ProductQueryContext<ProductShowQueryKeyEntries>): Promise<ProductResult> {
  const response = await rawAxios.get<ProductShow>(
    `/products/${productId}`,
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
  const nextPriceAdjustmentChangeAt: unknown = response.headers[NEXT_PRICING_BOUNDARY_HEADER]

  return attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt)
}

export async function getProductSearchTags({
  queryKey: [{ organizationId }],
}: ProductQueryContext<ProductSearchTagsQueryKeyEntries>) {
  return (
    (
      await axios.get<{ data: ProductSearchTagGroup[] }>(
        '/products/search-tags',
        buildAdminRequestConfig({ organizationId })
      )
    )?.data ?? []
  )
}

interface RecommendSearchTagsOptions {
  /** Also recommend tags with weaker text matches. */
  includeSoftTags?: boolean
}

/** Recommends tags for a product that isn't saved yet; nothing is applied. */
interface RecommendDraftSearchTagsValues extends RecommendSearchTagsOptions {
  draft: ProductSearchTagDraft
  productId?: undefined
  apply?: undefined
  replace?: undefined
  searchTagIds?: undefined
}

/**
 * Recommends tags for a saved product. By default the recommendation is applied; `searchTagIds`
 * saves reviewed tags instead, and `apply: false` only previews.
 */
interface RecommendProductSearchTagsForProductValues extends RecommendSearchTagsOptions {
  draft?: undefined
  productId: ProductIdInput
  apply?: boolean
  /** Replace the product's search tags instead of adding to them. */
  replace?: boolean
  searchTagIds?: number[]
}

export type RecommendProductSearchTagsValues =
  | RecommendDraftSearchTagsValues
  | RecommendProductSearchTagsForProductValues

export async function recommendProductSearchTags(
  values: RecommendProductSearchTagsValues & ProductOrganizationScope
): Promise<ProductDraftSearchTagRecommendation | ProductSearchTagRecommendationResult> {
  const { organizationId, includeSoftTags = false } = values

  if (values.draft) {
    return axios.post<ProductDraftSearchTagRecommendation>(
      '/products/search-tags/recommend',
      {
        include_soft_tags: includeSoftTags,
        draft: values.draft,
      },
      buildAdminRequestConfig({ organizationId })
    )
  }

  const { apply = true, productId, replace = false, searchTagIds } = values

  return axios.post<ProductSearchTagRecommendationResult>(
    `/products/${productId}/search-tags/recommend`,
    {
      apply,
      include_soft_tags: includeSoftTags,
      replace,
      ...(Array.isArray(searchTagIds) ? { search_tag_ids: searchTagIds } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createProduct({
  organizationId,
  ...values
}: CreateProductRequest & ProductOrganizationScope) {
  return axios.post<CreatedProduct>(
    '/products',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Resolves to the saved product. */
export async function updateProduct({
  organizationId,
  productId,
  ...values
}: UpdateProductRequest & ProductScope) {
  return axios.put<ProductDetail>(
    `/products/${productId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function softDeleteProduct({ organizationId, productId }: ProductScope) {
  return axios.delete<string>(`/products/${productId}`, buildAdminRequestConfig({ organizationId }))
}
